import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Box, IconButton } from '@mui/material';
import { Close } from '@mui/icons-material';
import ErrorBoundary from 'components/shared/error-boundary/ErrorBoundary';
import StyledNotificationRow from 'common/theme/notification/StyledNotificationRow';
import { getNotificationType } from 'common/utils/notification';

const EASE = 'cubic-bezier(0.32, 0.72, 0, 1)';

const clickableSx = {
  cursor: 'pointer',
  display: 'flex',
  flex: 1,
  minWidth: 0,
  textAlign: 'left',
  transition: `transform 200ms ${EASE}`,
  '&:active': { transform: 'scale(0.99)' },
  '&:focus-visible': {
    borderRadius: '10px',
    outline: '2px solid',
    outlineColor: 'gold.main',
    outlineOffset: 2
  }
};

function NotificationMenuItem({ notification, expired = false, onItemClick, onDelete }) {
  const { t } = useTranslation(['notifications']);
  const content = getNotificationType(notification);

  // Tipo desconocido (o sin datos para pintar): mejor omitir la fila que dejarla vacía.
  if (!content) return null;

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onItemClick(notification);
    }
  };

  return (
    <StyledNotificationRow component="li" read={notification.read} expired={expired}>
      <Box
        role="button"
        tabIndex={0}
        onClick={() => onItemClick(notification)}
        onKeyDown={handleKeyDown}
        sx={clickableSx}>
        {/* Aísla cada notificación: si una viene malformada y su componente lanza, se
            omite ESA fila en vez de tumbar toda la lista y hacer desaparecer la campana. */}
        <ErrorBoundary name="notification-item" fallback={() => null}>
          {content}
        </ErrorBoundary>
      </Box>
      <IconButton
        className="notification-row-delete"
        size="small"
        aria-label={t('deleteOne', { ns: 'notifications', defaultValue: 'Eliminar notificación' })}
        onClick={(event) => {
          event.stopPropagation();
          onDelete(notification);
        }}
        sx={{
          // `text.disabled` no llega al 3:1 que pide un control: la X quedaba casi invisible.
          color: 'text.secondary',
          flex: 'none',
          transition: `color 200ms ${EASE}, transform 200ms ${EASE}`,
          '&:hover': { color: 'sessionStatus.absent.fg' },
          '&:active': { transform: 'scale(0.92)' }
        }}>
        <Close sx={{ fontSize: 16 }} />
      </IconButton>
    </StyledNotificationRow>
  );
}

NotificationMenuItem.propTypes = {
  expired: PropTypes.bool,
  notification: PropTypes.shape({
    id: PropTypes.string.isRequired,
    read: PropTypes.bool.isRequired
  }).isRequired,
  onDelete: PropTypes.func.isRequired,
  onItemClick: PropTypes.func.isRequired
};

export default NotificationMenuItem;
